import { PDFViewerWithFields, FieldPropertiesEditor } from "@repo/docs"
import { FIELD_TYPES } from "@repo/docs"
import type { FieldType, TemplateField } from "@repo/docs"
import { Button } from "@repo/shad/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@repo/shad/ui/card"
import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

export const Route = createFileRoute("/test-fields")({
  component: TestFieldsPage,
})

function TestFieldsPage() {
  const [fields, setFields] = useState<TemplateField[]>([])
  const [selectedFieldType, setSelectedFieldType] = useState<FieldType | undefined>("text")
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)

  const handleFieldCreate = (
    field: Omit<TemplateField, "_id" | "createdAt" | "updatedAt" | "revision">,
  ) => {
    const newField: TemplateField = {
      ...field,
      _id: `field_${Date.now()}`,
      revision: 0,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }
    setFields([...fields, newField])
  }

  const handleFieldUpdate = (fieldId: string, updates: Partial<TemplateField>) => {
    setFields(
      fields.map((f) =>
        f._id === fieldId
          ? { ...f, ...updates, revision: (f.revision ?? 0) + 1, updatedAt: Date.now() }
          : f,
      ),
    )
  }

  const handleFieldDelete = (fieldId: string) => {
    setFields(fields.filter((f) => f._id !== fieldId))
  }

  return (
    <div className="h-screen flex">
      <div className="w-80 border-r p-4 space-y-4">
        <h1 className="text-2xl font-bold">Field Mapping Test</h1>

        <Card>
          <CardHeader>
            <CardTitle>Field Type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground mb-2">
              Select a field type then click and drag on the PDF to create a field
            </p>
            <div className="grid grid-cols-1 gap-2">
              {FIELD_TYPES.map((type) => (
                <Button
                  key={type}
                  variant={selectedFieldType === type ? "default" : "outline"}
                  className="w-full justify-start gap-2"
                  onClick={() => setSelectedFieldType(type)}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </Button>
              ))}
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => setSelectedFieldType(undefined)}
            >
              Clear Selection
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Fields ({fields.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {fields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No fields added yet</p>
            ) : (
              <div className="space-y-1">
                {fields.map((field) => (
                  <Button
                    key={field._id}
                    variant={selectedFieldId === field._id ? "default" : "outline"}
                    className="w-full justify-start text-sm"
                    onClick={() => setSelectedFieldId(field._id)}
                  >
                    <span className="capitalize mr-2">{field.fieldType}:</span>
                    {field.label || field.fieldName}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {selectedFieldId && fields.find((f) => f._id === selectedFieldId) && (
          <FieldPropertiesEditor
            field={fields.find((f) => f._id === selectedFieldId)!}
            onUpdate={handleFieldUpdate}
            onClose={() => setSelectedFieldId(null)}
            onDelete={handleFieldDelete}
          />
        )}
      </div>

      <div className="flex-1">
        <PDFViewerWithFields
          pdfUrl="https://mozilla.github.io/pdf.js/web/compressed.tracemonkey-pldi-09.pdf"
          fields={fields}
          selectedFieldType={selectedFieldType}
          onFieldCreate={handleFieldCreate}
          onFieldUpdate={handleFieldUpdate}
          onFieldSelect={setSelectedFieldId}
          isEditMode={true}
          showGrid={true}
          showGuides={true}
          className="h-full"
        />
      </div>
    </div>
  )
}
